import {
  ProjectCandidateStageCategory,
  ProjectGetCandidateStageBreakdownQuery,
} from 'generated/graphql';
import {
  STAGE_CATEGORIES_WITH_COMPLETION,
  STAGE_CATEGORY_LABELS,
  STAGE_CATEGORY_ORDER,
} from '../../Projects.constants';

export type CandidateStageBreakdown =
  ProjectGetCandidateStageBreakdownQuery['ProjectGetCandidateStageBreakdown'];

/**
 * The tooltip's explanation of what the rows count, shared with the
 * screen-reader label so the two cannot drift. The progression statuses are
 * worth spelling out: the breakdown counts every candidate on the role at the
 * stage they are currently at, so a rejected or hired one still shows in the
 * stage they ended at and the rows add up to the role's total.
 */
export const CANDIDATE_STAGES_HINT =
  'Every candidate at their current stage — active, hired or rejected';

/**
 * The Interview row's second line. It has three things to say, because the
 * count comes from a query of its own: the number, that it is still coming, or
 * that the meeting service could not be reached. Shown even at zero, so the
 * absence of interviews is a fact rather than a missing line.
 */
export const getInterviewLinkDetail = ({
  hasError,
  interviewLinkCount,
  isLoading,
}: {
  hasError: boolean;
  interviewLinkCount?: number;
  isLoading: boolean;
}): string => {
  if (hasError) return 'Linked to an interview unavailable';
  if (isLoading || interviewLinkCount === undefined) {
    return 'Linked to an interview …';
  }
  return `Linked to an interview ${interviewLinkCount}`;
};

/**
 * The breakdown rows in the order the tooltip lists them. The backend returns a
 * row per kind of stage it found candidates at, in no particular order, and
 * nothing at all for a stage nobody is at — so the rows for the role's own
 * empty stages are made here, as zeros. A kind that is neither a stage on the
 * role nor anywhere a candidate sits is left out.
 */
export const sortBreakdownRows = (
  breakdown: CandidateStageBreakdown,
  roleStageCategories: ProjectCandidateStageCategory[]
): CandidateStageBreakdown =>
  STAGE_CATEGORY_ORDER.map(category => {
    const breakdownRow = breakdown.find(row => row.category === category);

    if (breakdownRow && breakdownRow.candidatesCount > 0) {
      return breakdownRow;
    }

    if (!roleStageCategories.includes(category)) {
      return undefined;
    }

    // Completion is counted on the stage a candidate is at, so an empty stage
    // has completed none. Null everywhere else, the way the backend reports a
    // category that does not record completion at all.
    return {
      category,
      candidatesCount: 0,
      completedCount: STAGE_CATEGORIES_WITH_COMPLETION.includes(category)
        ? 0
        : null,
    };
  }).filter((row): row is CandidateStageBreakdown[number] => Boolean(row));

export const getCandidatesAriaLabel = (
  candidatesCount: number,
  roleStageCategories: ProjectCandidateStageCategory[],
  breakdown?: CandidateStageBreakdown
): string => {
  const total = `${candidatesCount} candidates on role`;

  if (!breakdown) {
    return `${total}. ${CANDIDATE_STAGES_HINT}. Loads on hover.`;
  }

  const rows = sortBreakdownRows(breakdown, roleStageCategories)
    .map(row => `${STAGE_CATEGORY_LABELS[row.category]} ${row.candidatesCount}`)
    .join(', ');

  return `${total}. ${CANDIDATE_STAGES_HINT}: ${rows}`;
};
